/**
 * In-app preview Assistant API (builder backend `/api/assistant/*`).
 *
 * Powers the floating Assistant chat button in the Run/Preview surface
 * (EndUserAppView). Non-streaming calls go through the shared axios client
 * (cookie auth); the ask stream uses fetch so we can read the SSE body.
 */
import api from './client'
import { APP_BASE } from '@/config/basePath'

export interface AssistantConfig {
  enabled: boolean
  display_name?: string
  greeting?: string
  suggested_questions?: string[]
  capabilities?: string[]
  business_areas?: { name: string; description: string }[]
  /** Why the assistant is off for this caller, when it is: 'profile_not_granted'
   *  (their profile isn't on the designer's access list). */
  reason?: string
  /** False when no AI is configured for the caller's organization — the
   *  Assistant is granted but cannot answer until a func-admin configures it. */
  ai_configured?: boolean
  /** Human-readable instruction shown in place of the composer when
   *  `ai_configured` is false. */
  ai_notice?: string
  /** True when at least one usable intent accepts SCANNED input (QR/barcode)
   *  — the chat shows a Scan button. Deterministic scans run without the LLM. */
  scan_intents?: boolean
}

/** A fresh id for one Assistant conversation — sent as `conversation_id` so a
 *  call_ioflow intent can report which conversation it came from (its UUID).
 *  Falls back where `crypto.randomUUID` is absent (non-secure contexts). */
export function newAssistantConversationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

export interface AssistantAskBody {
  messages: { role: string; content: string }[]
  /** The conversation this turn belongs to (a call_ioflow intent's UUID). */
  conversation_id?: string
  business_area?: string
  /** Designer preview-as-profile (Configure AI → Preview). Edit rights required. */
  as_roles?: string[]
  /** Raw text decoded from a QR/barcode the user scanned this turn. */
  scan?: string
  /**
   * The object the reader currently has OPEN.
   *
   * Used for one thing: a document repository on screen narrows document
   * search to that repository. Somebody standing in the Controlled SOP
   * Repository who asks what the procedure says means that shelf, not every
   * shelf the subject area happens to span.
   */
  obj_name?: string
  /** Data files attached to this turn — ids from `assistantImportUpload`. */
  imports?: string[]
}

/** Result of a deterministic (no-LLM) intent run — scans routed by the server,
 *  and transaction-form 'assistant' action buttons. */
export interface AssistantIntentRunResult {
  ok: boolean
  reply?: string
  payloads?: Record<string, unknown>[]
  /** The intent asks for confirmation first — re-call with confirmed: true. */
  needs_confirmation?: boolean
  prompt?: string
  error?: string
  /** Missing parameters, as pre-filled questions (answer → re-run the intent). */
  questions?: { id: string; question: string; suggested: string; options?: string[]; why?: string }[]
  intent_id?: string
  parameters?: Record<string, unknown>
}

export async function assistantRunIntent(
  project: string,
  body: {
    intent_id: string; parameters?: Record<string, unknown>; confirmed?: boolean; as_roles?: string[]
    conversation_id?: string
  },
): Promise<AssistantIntentRunResult> {
  // This client is always a browser (a call_ioflow intent's IS_BROWSER).
  const res = await api.post('/assistant/run-intent', { project, ...body, is_browser: true })
  return res.data as AssistantIntentRunResult
}

/** Result of running a confirmed data-change action through the object's API. */
export interface AssistantRunResult {
  ok: boolean
  action: string
  obj_name: string
  result?: unknown
  error?: string
}

/** A streamed Assistant event: text blocks (`text`) or streamed chunks
 *  (`text_delta`, from the CLI engines), transient `activity` lines, tool
 *  activity, inline payloads (rows / visual / action), then `done`. */
export interface AssistantEvent {
  type: 'text' | 'text_delta' | 'activity' | 'tool_start' | 'tool_end' | 'done' | 'error'
  text?: string
  /** text_delta only: this chunk starts a NEW assistant message (bubble). */
  new_block?: boolean
  name?: string
  summary?: string
  payload?: Record<string, unknown>
  reply?: string
  detail?: string
}

export async function assistantConfig(
  project: string, asRoles?: string[],
): Promise<AssistantConfig> {
  const params: Record<string, string> = { project }
  if (asRoles?.length) params.as_roles = asRoles.join(',')
  const res = await api.get('/assistant/config', { params })
  return res.data as AssistantConfig
}

export async function assistantRunAction(
  project: string,
  body: { obj_name: string; action: string; values: Record<string, unknown>; publish?: boolean },
): Promise<AssistantRunResult> {
  const res = await api.post('/assistant/run-action', { project, ...body })
  return res.data as AssistantRunResult
}

/** Result of revising Assistant-drafted content from the review panel. */
export interface AssistantComposeRefineResult {
  ok: boolean
  values?: Record<string, unknown>
  notes?: string[]
  error?: string
}

/** Revise drafted content with the person's feedback — nothing is saved. The
 *  writer can take a minute or more on a long article, hence the timeout. */
export async function assistantComposeRefine(
  project: string,
  body: { obj_name: string; values: Record<string, unknown>; feedback: string },
): Promise<AssistantComposeRefineResult> {
  const res = await api.post('/assistant/compose-refine', { project, ...body }, { timeout: 300_000 })
  return res.data as AssistantComposeRefineResult
}

// ── Personal saved visuals ────────────────────────────────────────────────
// Assistant charts are ephemeral; the user may SAVE one to their private
// collection, re-run it, and (as owner, in application design) STANDARDISE it
// into a first-class Visual (V) object.

export interface SavedVisualMeta {
  id: string
  title: string
  chart_type?: string
  business_area?: string
  question?: string
  created_at?: string
  standardized_as?: string
}

export interface SavedVisualData {
  jsonData: unknown
  visualLayout: Record<string, unknown>
  optionsArr: unknown[]
  chartType?: string
  title?: string
  rowCount?: number
}

/** The "recipe" needed to persist a rendered visual — carried on the visual
 *  payload from render_visual. */
export interface VisualSaveInput {
  title?: string
  question?: string
  business_area?: string
  spec?: Record<string, unknown>
  visual_layout?: Record<string, unknown>
  chart_type?: string
}

export async function assistantSaveVisual(
  project: string, body: VisualSaveInput,
): Promise<{ ok: boolean; visual: SavedVisualMeta }> {
  const res = await api.post('/assistant/save-visual', { project, ...body })
  return res.data
}

export async function assistantSavedVisuals(project: string): Promise<SavedVisualMeta[]> {
  const res = await api.get('/assistant/saved-visuals', { params: { project } })
  return (res.data?.visuals as SavedVisualMeta[]) || []
}

export async function assistantSavedVisualData(
  project: string, id: string,
): Promise<SavedVisualData> {
  const res = await api.post('/assistant/saved-visual-data', { project, id })
  return res.data as SavedVisualData
}

export async function assistantDeleteSavedVisual(project: string, id: string): Promise<boolean> {
  const res = await api.post('/assistant/delete-saved-visual', { project, id })
  return !!res.data?.ok
}

export async function assistantStandardizeVisual(
  project: string, id: string, obj_name: string,
): Promise<{ ok: boolean; obj_name: string }> {
  const res = await api.post('/assistant/standardize-visual', { project, id, obj_name })
  return res.data
}

export async function assistantAsk(
  project: string, body: AssistantAskBody,
): Promise<{ reply: string; payloads: Record<string, unknown>[] }> {
  const res = await api.post('/assistant/ask', { project, ...body, is_browser: true })
  return res.data
}

export async function assistantAskStream(
  project: string, body: AssistantAskBody,
  onEvent: (ev: AssistantEvent) => void, signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${APP_BASE}/api/assistant/ask/stream`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ project, ...body, is_browser: true }), signal,
  })
  if (!res.ok) {
    let detail = `Assistant request failed (${res.status})`
    try { const j = await res.json(); detail = (j && j.detail) || detail } catch { /* non-JSON error */ }
    throw new Error(detail)
  }
  if (!res.body) return
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    let sep: number
    while ((sep = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, sep)
      buf = buf.slice(sep + 2)
      const dataLine = frame.split('\n').find((l) => l.startsWith('data:'))
      if (!dataLine) continue
      try { onEvent(JSON.parse(dataLine.slice(5).trim()) as AssistantEvent) } catch { /* ignore malformed frame */ }
    }
  }
}

// ── File import (side-by-side spreadsheet preview) ───────────────────────
// The chat stages an attached file at once; the agent maps and prepares it;
// the preview reads the file's rows and the prepared records back here.

export interface AssistantImportUpload {
  import_id: string
  filename: string
  sheet?: string | null
  sheets: string[]
  rows: number
  columns: number
  headers: string[]
}

export async function assistantImportUpload(project: string, file: File): Promise<AssistantImportUpload> {
  const form = new FormData()
  form.append('project', project)
  form.append('file', file)
  const res = await api.post('/assistant/import/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data as AssistantImportUpload
}

// The staged-import wire shapes are shared with every host (the downloaded
// runtime and J2EE serve the same preview) — defined once in page-runtime.
export type {
  ImportIssue, ImportCounts, PreparedImportRecord, PreparedImport, ImportFileGrid,
  ImportPreviewData as AssistantImportPreview,
} from '@twasta/page-runtime/txn/capture'
import type {
  ImportLearnBody, ImportLearnResult, ImportPreviewData,
} from '@twasta/page-runtime/txn/capture'

/** Open a password-protected PDF the Assistant could not read, with the
 *  password typed into the chat's password card (never sent to the AI).
 *  A wrong password rejects with `detail.code === 'password_required'`. */
export async function assistantImportUnlock(
  project: string, importId: string,
  body: { obj_name: string; password: string; remember: boolean; sender?: string },
): Promise<{ ok: boolean; password?: { used?: string; remembered?: boolean; note?: string } }> {
  const res = await api.post(`/assistant/import/${encodeURIComponent(importId)}/unlock`, { project, ...body })
  return res.data
}

/** Remember the matches kept or picked in a saved Assistant import. */
export async function assistantImportLearn(
  project: string, importId: string, body: ImportLearnBody,
): Promise<ImportLearnResult> {
  const res = await api.post(`/assistant/import/${encodeURIComponent(importId)}/learn`, { project, ...body })
  return res.data as ImportLearnResult
}

/** The attached document itself (the preview's "File" view). */
export async function assistantImportFile(project: string, importId: string, page?: number): Promise<Blob> {
  const res = await api.get(`/assistant/import/${encodeURIComponent(importId)}/file`, {
    params: { project, ...(page ? { page } : {}) }, responseType: 'blob',
  })
  return res.data as Blob
}

export async function assistantImportPreview(project: string, importId: string): Promise<ImportPreviewData> {
  const res = await api.get(`/assistant/import/${encodeURIComponent(importId)}`, { params: { project } })
  return res.data as ImportPreviewData
}
